import React, { useState, useEffect, useCallback } from "react";
import { Row, Col, Card, Space, Button, Statistic, Select, Switch } from "antd";
import {
  PlusOutlined,
  SyncOutlined,
  FileTextOutlined,
  TeamOutlined,
  GlobalOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import useCirculars from "../../../hooks/academics/useCirculars";
import useAcademicYears from "../../../hooks/common/useAcademicYears";
import useTerms from "../../../hooks/common/useTerms";
import CircularTable from "../../../components/academics/circulars/CircularTable";
import CircularFormModal from "../../../components/academics/circulars/CircularFormModal";
import CircularDetailDrawer from "../../../components/academics/circulars/CircularDetailDrawer";
import Utilities from "../../../dependencies/custom/react-utilities";
import domainSettings from "../../../dependencies/custom/settings";

const CircularsManagement = () => {
  const navigate = useNavigate();

  const {
    circulars,
    loading,
    fetchCirculars,
    fetchCircularDetail,
    createCircular,
    updateCircular,
    activateCircular,
    deactivateCircular,
    archiveCircular,
    hardDeleteCircular,
    uploadCircularFiles,
    deleteCircularFile,
  } = useCirculars();

  const { academicYears, fetchAcademicYears } = useAcademicYears();
  const { terms, fetchTerms } = useTerms();

  const [classes, setClasses] = useState([]);

  // Filters
  const [filterAcadyrID, setFilterAcadyrID] = useState(null);
  const [filterTermID, setFilterTermID] = useState(null);
  const [filterAudience, setFilterAudience] = useState(null);
  const [showArchived, setShowArchived] = useState(false);

  // Modal state
  const [modalVisible, setModalVisible] = useState(false);
  const [editingCircular, setEditingCircular] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingFileId, setDeletingFileId] = useState(null);

  // Drawer state
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [viewCircularId, setViewCircularId] = useState(null);

  const fetchClasses = useCallback(async () => {
    const res = await Utilities.fetchWithAuth(
      "GET",
      domainSettings.urls.bootstrap.classes,
      null,
      navigate,
    );
    if (res?.ok)
      setClasses(Array.isArray(res.data) ? res.data : res.data?.results || []);
  }, [navigate]);

  useEffect(() => {
    fetchAcademicYears();
    fetchTerms();
    fetchClasses();
  }, [fetchAcademicYears, fetchTerms, fetchClasses]);

  useEffect(() => {
    const params = {};
    if (filterAcadyrID) params.acadyrID = filterAcadyrID;
    if (filterTermID) params.termID = filterTermID;
    if (filterAudience) params.audience = filterAudience;
    if (showArchived) params.include_inactive = true;
    fetchCirculars(params);
  }, [
    filterAcadyrID,
    filterTermID,
    filterAudience,
    showArchived,
    fetchCirculars,
  ]);

  // Statistics
  const totalCirculars = circulars.length;
  const wholeSchoolCount = circulars.filter(
    (c) => c.audience === "Whole School",
  ).length;
  const selectedClassesCount = circulars.filter(
    (c) => c.audience === "Selected Classes",
  ).length;
  const currentTermCount = filterTermID
    ? circulars.length
    : circulars.filter((c) => c.term).length;

  const handleView = (record) => {
    setViewCircularId(record.id);
    setDrawerVisible(true);
  };

  const handleEdit = async (record) => {
    const detail = await fetchCircularDetail(record.id);
    if (detail) {
      setEditingCircular(detail);
      setModalVisible(true);
    }
  };

  const handleActivate = async (record) => {
    const ok = await activateCircular(record.id);
    if (ok) refresh();
  };

  const handleDeactivate = async (record) => {
    const ok = await deactivateCircular(record.id);
    if (ok) refresh();
  };

  const handleArchive = async (record) => {
    const ok = await archiveCircular(record.id);
    if (ok) refresh();
  };

  const handleDelete = async (record) => {
    const ok = await hardDeleteCircular(record.id);
    if (ok) refresh();
  };

  const handleSubmit = async (formData, id) => {
    setSaving(true);
    let result;
    if (id) {
      result = await updateCircular(id, formData);
    } else {
      result = await createCircular(formData);
    }
    setSaving(false);
    if (result) {
      setModalVisible(false);
      setEditingCircular(null);
      refresh();
    }
  };

  const handleDeleteFileInModal = async (circularId, fileId) => {
    setDeletingFileId(fileId);
    const ok = await deleteCircularFile(circularId, fileId);
    setDeletingFileId(null);
    if (ok && editingCircular) {
      setEditingCircular((prev) => ({
        ...prev,
        files: prev.files.filter((f) => f.id !== fileId),
      }));
    }
  };

  const refresh = () => {
    const params = {};
    if (filterAcadyrID) params.acadyrID = filterAcadyrID;
    if (filterTermID) params.termID = filterTermID;
    if (filterAudience) params.audience = filterAudience;
    if (showArchived) params.include_inactive = true;
    fetchCirculars(params);
  };

  return (
    <div style={{ padding: 24 }}>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Total Circulars"
              value={totalCirculars}
              prefix={<FileTextOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Whole School"
              value={wholeSchoolCount}
              prefix={<GlobalOutlined />}
              valueStyle={{ color: "#52c41a" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="Selected Classes"
              value={selectedClassesCount}
              prefix={<TeamOutlined />}
              valueStyle={{ color: "#1677ff" }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={6}>
          <Card>
            <Statistic
              title="With Term Filter"
              value={currentTermCount}
              prefix={<CalendarOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Card */}
      <Card
        title="Circulars"
        extra={
          <Space>
            <Button icon={<SyncOutlined />} onClick={refresh} loading={loading}>
              Refresh
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingCircular(null);
                setModalVisible(true);
              }}
            >
              New Circular
            </Button>
          </Space>
        }
      >
        {/* Filters */}
        <Row gutter={12} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Academic Year"
              allowClear
              style={{ width: "100%" }}
              value={filterAcadyrID}
              onChange={setFilterAcadyrID}
              showSearch
              optionFilterProp="label"
            >
              {academicYears.map((y) => (
                <Select.Option key={y.id} value={y.id} label={y.acadyr}>
                  {y.acadyr}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Term"
              allowClear
              style={{ width: "100%" }}
              value={filterTermID}
              onChange={setFilterTermID}
              showSearch
              optionFilterProp="label"
            >
              {terms.map((t) => (
                <Select.Option key={t.id} value={t.id} label={t.name}>
                  {t.name}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={12} md={5}>
            <Select
              placeholder="Audience"
              allowClear
              style={{ width: "100%" }}
              value={filterAudience}
              onChange={setFilterAudience}
            >
              <Select.Option value="Whole School">Whole School</Select.Option>
              <Select.Option value="Selected Classes">
                Selected Classes
              </Select.Option>
            </Select>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Space>
              <Switch
                checked={showArchived}
                onChange={setShowArchived}
                size="small"
              />
              <span style={{ fontSize: 13 }}>Show Inactive/Archived</span>
            </Space>
          </Col>
        </Row>

        <CircularTable
          circulars={circulars}
          loading={loading}
          onView={handleView}
          onEdit={handleEdit}
          onActivate={handleActivate}
          onDeactivate={handleDeactivate}
          onArchive={handleArchive}
          onDelete={handleDelete}
        />
      </Card>

      <CircularFormModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setEditingCircular(null);
        }}
        onSubmit={handleSubmit}
        onDeleteFile={handleDeleteFileInModal}
        saving={saving}
        deletingFileId={deletingFileId}
        circular={editingCircular}
        academicYears={academicYears}
        terms={terms}
        classes={classes}
      />

      <CircularDetailDrawer
        visible={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        circularId={viewCircularId}
        onFetchDetail={fetchCircularDetail}
        onDeleteFile={deleteCircularFile}
        onUploadFiles={uploadCircularFiles}
      />
    </div>
  );
};

export default CircularsManagement;
